/**
 * The per-incident pages on airisk.mit.edu (/ai-incidents/<slug>), shared by
 * the Webflow sync (which creates the items) and the tracker UI (which links
 * to them). Browser-safe: no Node imports, so the embed can use it.
 *
 * The slug is computed from the incident id and title with the same function
 * the sync used when it CREATED the item, so the tracker needs no lookup. An
 * item keeps its slug for life, so a later title edit does not move the page;
 * `incidentPageUrl` therefore only matches an item whose title has not
 * changed since creation. Pages exist for the Arcola dataset only.
 */

import { SITE_ORIGIN, slugFrom } from './sitePages';

export { SITE_ORIGIN };
export const COLLECTION_PATH = '/ai-incidents';
export const TRACKER_VIEW_URL = `${SITE_ORIGIN}/ai-incident-tracker/incident-view`;

/** The dataset the pages are generated from (API `src` param). */
export const INCIDENT_PAGES_DATASET = 'incidents-arcola';

/**
 * Whether the pages are on production (site published 8 Oct 2026). While
 * false the tracker shows no links to the pages, so nothing points at a 404.
 */
export const INCIDENT_PAGES_LIVE = true;

export function stripLeadingId(title: string): string {
  return title.replace(/^\s*\d+\s*[.:)]\s+/, '').trim();
}

/**
 * `<id>-<title words>`, at most 60 characters, never ending on a stopword
 * (lib/sitePages), after dropping a leading "12: " from the title. The sync
 * uses it only when an item is CREATED: an existing item keeps its slug for life.
 */
export function slugFor(id: string, title: string): string {
  return slugFrom(id, stripLeadingId(title));
}

export function pageUrl(slug: string): string {
  return `${SITE_ORIGIN}${COLLECTION_PATH}/${slug}`;
}

/** The page for an incident, or '' when pages are not live or the dataset has none. */
export function incidentPageUrl(id: string, title: string, dataset?: string, live = INCIDENT_PAGES_LIVE): string {
  if (!live || dataset !== INCIDENT_PAGES_DATASET || !/^\d+$/.test(id)) return '';
  return pageUrl(slugFor(id, title));
}
